/**
 * client/src/structure/BlockMode.tsx — §3.3's gate, for the book that has no chapters.
 *
 * **A gate that renders one row is not a gate.** That sentence is §3.3's, and this file is what it
 * demands. The most common real-world file — a Calibre conversion from `.docx`, a Gutenberg export —
 * is one XHTML item in the spine with every paragraph `<p class="calibre1">`, chapter titles as
 * slightly larger centred paragraphs, no `<h1>` and no landmarks. Heading inference catches most of
 * them; when it does not, a 400-page novel arrives as **one chapter**, the chapter list is a single
 * card, the "the book starts here" rail has nowhere to move to, and the screen that exists to let a
 * human fix the structure offers them nothing. §3.3 caps `bodyStartConfidence` at 0.4 for exactly
 * that shape and says the editor must work on **blocks** instead.
 *
 * So: the blocks, windowed, searchable, each one able to become where the book begins or where a
 * chapter starts. Windowed because the shape this exists for is precisely the one with twenty
 * thousand blocks in a single chapter — rendering them all is a forced layout of the whole book on a
 * phone, which is the same mistake §5.1 spends a cost guard avoiding in the reader.
 */

import { useEffect, useState } from "react";

import { ApiError, api } from "../lib/api.ts";
import type { RestructureOp } from "./ChapterList.tsx";

interface BlockRow {
  id: string;
  chapterOrd: number;
  ord: number;
  kind: string;
  level: number | null;
  text: string;
  chars: number;
  /** Null when there is nothing sayable in this block — an image, a scene rule, an empty caption. */
  firstSid: string | null;
  isChapterStart: boolean;
  isBodyStart: boolean;
}

interface BlockPage {
  editionId: string;
  total: number;
  from: number;
  limit: number;
  blocks: BlockRow[];
}

const PAGE = 60;

export interface BlockModeProps {
  editionId: string;
  chapterCount: number;
  ops: RestructureOp[];
  onStage: (ops: RestructureOp[]) => void;
  onUnstage: (predicate: (op: RestructureOp) => boolean) => void;
  onPatch: (patch: Record<string, unknown>) => Promise<void>;
  busy: boolean;
}

export function BlockMode({ editionId, chapterCount, ops, onStage, onUnstage, onPatch, busy }: BlockModeProps) {
  const [from, setFrom] = useState(0);
  const [query, setQuery] = useState("");
  const [applied, setApplied] = useState("");
  const [headingsOnly, setHeadingsOnly] = useState(false);
  const [data, setData] = useState<BlockPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    const params = new URLSearchParams({ from: String(from), limit: String(PAGE) });
    if (applied !== "") params.set("q", applied);
    if (headingsOnly) params.set("headings", "1");
    api
      .get<BlockPage>(`/editions/${encodeURIComponent(editionId)}/blocks?${params.toString()}`)
      .then((page) => {
        if (!live) return;
        setData(page);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!live) return;
        setError(err instanceof ApiError ? err.message : String(err));
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [editionId, from, applied, headingsOnly]);

  const staged = new Map(ops.flatMap((o) => (o.op === "split" ? [[o.blockId, o.title ?? ""] as const] : [])));
  const total = data?.total ?? 0;

  return (
    <section className="ab-panel">
      <h2 className="ab-h2">This book has no chapters the parser could find</h2>
      <p className="ab-hint">
        {chapterCount === 1
          ? "The whole book came through as one chapter"
          : `The whole book came through as ${chapterCount} chapters`}
        , which usually means a Calibre or Gutenberg export with no real headings in it — the titles
        are ordinary paragraphs in a bigger font. So the editor works on blocks: find the line a
        chapter starts at and say so, and find the line the book starts at and say that.
      </p>

      <form
        className="ab-actions"
        onSubmit={(e) => {
          e.preventDefault();
          setFrom(0);
          setApplied(query.trim());
        }}
      >
        <div className="ab-search" style={{ flex: "1 1 14rem" }}>
          <input
            value={query}
            placeholder="Find a line — &ldquo;It is a truth universally&rdquo;"
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Find a line"
          />
        </div>
        <button type="submit" className="ab-btn ab-btn-ghost ab-btn-sm">
          Find
        </button>
        <label className="ab-check">
          <input
            type="checkbox"
            checked={headingsOnly}
            onChange={(e) => {
              setFrom(0);
              setHeadingsOnly(e.target.checked);
            }}
          />
          {/* The first thing to try. If the parser found any headings at all, the chapter starts are
              almost certainly among them. */}
          <span>Only lines it thinks are headings</span>
        </label>
      </form>

      {error !== null && <p className="ab-hint ab-hint-bad">{error}</p>}
      {loading && data === null && <p className="ab-hint">Loading the book…</p>}

      {data !== null && data.blocks.length === 0 && (
        <p className="ab-hint">
          {applied === "" ? "There is nothing here." : `Nothing in this book matches “${applied}”.`}
        </p>
      )}

      <ol className="ab-chapters">
        {(data?.blocks ?? []).map((b) => {
          const stagedTitle = staged.get(b.id);
          return (
            <li key={b.id}>
              <article className={b.isBodyStart ? "ab-chapter is-body" : "ab-chapter"}>
                <header>
                  <span className="ab-chip ab-chip-quiet">{b.kind}</span>
                  {b.isChapterStart && <span className="ab-chip ab-chip-work">chapter starts here</span>}
                  {b.isBodyStart && <span className="ab-chip ab-chip-voice">the book starts here</span>}
                  <span className="ab-chapter-count">{b.chars.toLocaleString()} chars</span>
                </header>
                <p className="ab-chapter-preview">{b.text || <em>no text — an image or a scene break</em>}</p>
                <code className="ab-sid">{b.id}</code>

                {stagedTitle !== undefined ? (
                  <div className="ab-actions">
                    <span className="ab-chip ab-chip-work">Will start a chapter: &ldquo;{stagedTitle}&rdquo;</span>
                    <button
                      type="button"
                      className="ab-btn ab-btn-ghost ab-btn-sm"
                      onClick={() => onUnstage((o) => o.op === "split" && o.blockId === b.id)}
                    >
                      Undo
                    </button>
                  </div>
                ) : (
                  <div className="ab-actions">
                    {b.firstSid !== null && !b.isBodyStart && (
                      <button
                        type="button"
                        className="ab-btn ab-btn-ghost ab-btn-sm ab-btn-wrap"
                        disabled={busy}
                        onClick={() => void onPatch({ bodyStartSid: b.firstSid })}
                      >
                        Start the book here
                      </button>
                    )}
                    {!b.isChapterStart && (
                      <StartChapter
                        suggested={b.kind === "heading" ? b.text.trim().slice(0, 120) : ""}
                        onStage={(title) => onStage([{ op: "split", blockId: b.id, title }])}
                      />
                    )}
                  </div>
                )}
              </article>
            </li>
          );
        })}
      </ol>

      {total > PAGE && (
        <div className="ab-actions">
          <button
            type="button"
            className="ab-btn ab-btn-ghost ab-btn-sm"
            disabled={from === 0}
            onClick={() => setFrom(Math.max(0, from - PAGE))}
          >
            Back
          </button>
          <span className="ab-hint ab-hint-quiet">
            {from + 1}–{Math.min(from + PAGE, total)} of {total.toLocaleString()}
          </span>
          <button
            type="button"
            className="ab-btn ab-btn-ghost ab-btn-sm"
            disabled={from + PAGE >= total}
            onClick={() => setFrom(from + PAGE)}
          >
            More
          </button>
        </div>
      )}
    </section>
  );
}

/**
 * "A chapter starts here", with its title.
 *
 * The title is asked for rather than invented. A block that is a heading suggests its own text; one
 * that is not has nothing to borrow, and an untitled chapter is a blank row in the drawer and a blank
 * lock screen — so the server refuses one, and this asks before it sends.
 */
function StartChapter({ suggested, onStage }: { suggested: string; onStage: (title: string) => void }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(suggested);

  if (!open) {
    return (
      <button type="button" className="ab-btn ab-btn-ghost ab-btn-sm ab-btn-wrap" onClick={() => setOpen(true)}>
        A chapter starts here
      </button>
    );
  }
  const clean = title.trim();
  return (
    <div className="ab-field" style={{ flex: "1 1 100%" }}>
      <label htmlFor={`ab-newchap-${suggested.slice(0, 6)}`}>Call the new chapter</label>
      <input
        id={`ab-newchap-${suggested.slice(0, 6)}`}
        value={title}
        autoFocus
        maxLength={300}
        placeholder="Chapter Nine"
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setOpen(false);
          if (e.key === "Enter" && clean !== "") {
            onStage(clean);
            setOpen(false);
          }
        }}
      />
      <div className="ab-actions">
        <button
          type="button"
          className="ab-btn ab-btn-primary ab-btn-sm"
          disabled={clean === ""}
          onClick={() => {
            onStage(clean);
            setOpen(false);
          }}
        >
          Stage it
        </button>
        <button type="button" className="ab-btn ab-btn-ghost ab-btn-sm" onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
    </div>
  );
}
